import React, { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle, Plus, X, Clock, User,
  Activity, CheckCircle, XCircle, Circle, Zap, Server,
  Wifi, Mail, Monitor, Search, ChevronRight,
  MessageSquare, RefreshCw, Shield, Loader2, UserCheck
} from "lucide-react";
import config from "../config";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const SEVERITIES = [
  { value: "P1", label: "P1 Critical", pill: "bg-red-500/15 text-red-400 ring-1 ring-red-500/30",             dot: "bg-red-400",    strip: "bg-red-500"    },
  { value: "P2", label: "P2 High",     pill: "bg-orange-500/15 text-orange-400 ring-1 ring-orange-500/30",    dot: "bg-orange-400", strip: "bg-orange-500" },
  { value: "P3", label: "P3 Medium",   pill: "bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30",       dot: "bg-amber-400",  strip: "bg-amber-400"  },
  { value: "P4", label: "P4 Low",      pill: "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30", dot: "bg-emerald-400", strip: "bg-emerald-500" },
];

const STATUSES = [
  { value: "OPEN",        label: "Open",        icon: <Circle className="w-3 h-3" />,      pill: "bg-sky-500/15 text-sky-400 ring-1 ring-sky-500/30"              },
  { value: "IN_PROGRESS", label: "In Progress", icon: <RefreshCw className="w-3 h-3" />,   pill: "bg-violet-500/15 text-violet-400 ring-1 ring-violet-500/30"    },
  { value: "RESOLVED",    label: "Resolved",    icon: <CheckCircle className="w-3 h-3" />, pill: "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30" },
  { value: "CLOSED",      label: "Closed",      icon: <CheckCircle className="w-3 h-3" />, pill: "bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30"       },
  { value: "CANCELLED",   label: "Cancelled",   icon: <XCircle className="w-3 h-3" />,     pill: "bg-slate-500/10 text-slate-500 ring-1 ring-slate-500/20"       },
];

const INCIDENT_TYPES = [
  { value: "website_down",  label: "Website Down",          icon: <Wifi className="w-4 h-4" />     },
  { value: "system_access", label: "System Not Accessible", icon: <Monitor className="w-4 h-4" />  },
  { value: "email_issue",   label: "Email Not Sending",     icon: <Mail className="w-4 h-4" />     },
  { value: "server_crash",  label: "Server Crash",          icon: <Server className="w-4 h-4" />   },
  { value: "app_slow",      label: "Application Slow",      icon: <Activity className="w-4 h-4" /> },
  { value: "other",         label: "Other",                 icon: <Zap className="w-4 h-4" />      },
];

// ─── API LAYER ────────────────────────────────────────────────────────────────

const api = {
  async getAll(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v && v !== "ALL"))
    ).toString();
    const res = await fetch(`${config.API_BASE_URL}/incidents${qs ? `?${qs}` : ""}`);
    if (!res.ok) throw new Error("Failed to fetch incidents");
    return res.json();
  },
  async create(body) {
    const res = await fetch(`${config.API_BASE_URL}/incidents`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("Failed to create incident");
    return res.json();
  },
  async changeStatus(id, status, performedBy = "Admin") {
    const res = await fetch(`${config.API_BASE_URL}/incidents/${id}/status`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, performedBy }),
    });
    if (!res.ok) throw new Error("Failed to change status");
    return res.json();
  },
  async addNote(id, note, performedBy = "Admin") {
    const res = await fetch(`${config.API_BASE_URL}/incidents/${id}/notes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note, performedBy }),
    });
    if (!res.ok) throw new Error("Failed to add note");
    return res.json();
  },
  async assign(id, assignee, deadline, performedBy = "Admin") {
    const res = await fetch(`${config.API_BASE_URL}/incidents/${id}/assign`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assignee, deadline, performedBy }),
    });
    if (!res.ok) throw new Error("Failed to assign incident");
    return res.json();
  },
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function timeAgo(date) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const getSeverity = v => SEVERITIES.find(s => s.value === v) || SEVERITIES[3];
const getStatus   = v => STATUSES.find(s => s.value === v)   || STATUSES[0];
const getType     = v => INCIDENT_TYPES.find(t => t.value === v) || INCIDENT_TYPES[5];

// ─── BADGES ───────────────────────────────────────────────────────────────────

const SeverityBadge = ({ value }) => {
  const s = getSeverity(value);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      {s.label}
    </span>
  );
};

const StatusBadge = ({ value }) => {
  const s = getStatus(value);
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${s.pill}`}>
      {s.icon}{s.label}
    </span>
  );
};

// ─── SHARED STYLES ────────────────────────────────────────────────────────────

const inputCls =
  "w-full px-3.5 py-2.5 bg-[#0f1627] border border-[#243050] rounded-xl text-white text-sm outline-none " +
  "focus:ring-2 focus:ring-[#3b5bdb]/40 focus:border-[#3b5bdb]/60 placeholder-[#4a5878] transition";

const selectCls =
  "px-3.5 py-2.5 bg-[#0f1627] border border-[#243050] rounded-xl text-[#a8b8d8] text-sm outline-none " +
  "focus:ring-2 focus:ring-[#3b5bdb]/40";

// ─── ERROR TOAST ──────────────────────────────────────────────────────────────

const ErrorToast = ({ message, onDismiss }) => (
  <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 bg-red-600/90 text-white text-sm font-medium px-4 py-3 rounded-xl shadow-2xl border border-red-500/40 backdrop-blur-sm">
    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
    {message}
    <button onClick={onDismiss} className="ml-1 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
  </div>
);

// ─── MODAL SHELL ──────────────────────────────────────────────────────────────

const ModalShell = ({ children, maxW = "max-w-xl" }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className={`bg-[#131929] border border-[#1e2a42] rounded-2xl w-full ${maxW} shadow-2xl overflow-hidden`}>
      {children}
    </div>
  </div>
);

const ModalHeader = ({ icon, title, subtitle, onClose, extra }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e2a42] bg-[#0f1627]/80">
    <div className="flex items-center gap-2.5 flex-1 min-w-0">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-[#243050] bg-[#1a2540] flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-white">{title}</h3>
        {subtitle && <p className="text-xs text-[#6b7fa3]">{subtitle}</p>}
      </div>
    </div>
    <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
      {extra}
      <button onClick={onClose} className="p-1.5 hover:bg-[#1e2a42] rounded-lg transition-colors">
        <X className="w-4 h-4 text-[#6b7fa3]" />
      </button>
    </div>
  </div>
);

const ModalFooter = ({ children }) => (
  <div className="px-6 py-4 border-t border-[#1e2a42] bg-[#0f1627]/60 flex gap-3">
    {children}
  </div>
);

// ─── ASSIGN MODAL ─────────────────────────────────────────────────────────────

const AssignModal = ({ incident, onClose, onUpdate }) => {
  const [assignee, setAssignee] = useState(incident.assignee || "");
  const [deadline, setDeadline] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const sev      = getSeverity(incident.severity);
  const autoLabel = { P1: "4 hours", P2: "1 day", P3: "3 days", P4: "7 days" }[incident.severity] || "7 days";
  const suggested = (() => {
    const d = new Date();
    if      (incident.severity === "P1") d.setHours(d.getHours() + 4);
    else if (incident.severity === "P2") d.setDate(d.getDate() + 1);
    else if (incident.severity === "P3") d.setDate(d.getDate() + 3);
    else                                  d.setDate(d.getDate() + 7);
    return d;
  })();

  const sevAccent = {
    P1: "text-red-400 border-red-500/25 bg-red-500/10",
    P2: "text-orange-400 border-orange-500/25 bg-orange-500/10",
    P3: "text-amber-400 border-amber-500/25 bg-amber-500/10",
    P4: "text-emerald-400 border-emerald-500/25 bg-emerald-500/10",
  }[incident.severity] || "text-slate-400 border-slate-500/25 bg-slate-500/10";

  const handleSave = async () => {
    if (!deadline) { setError("Deadline is required before assigning."); return; }
    setLoading(true); setError(null);
    try {
      const updated = await api.assign(incident.id, assignee, new Date(deadline).toISOString());
      onUpdate(updated); onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <ModalShell maxW="max-w-md">
      <ModalHeader
        icon={<UserCheck className="w-4 h-4 text-[#74c0fc]" />}
        title="Assign Incident"
        subtitle={incident.incidentNumber ?? incident.id}
        onClose={onClose}
      />
      <div className="p-5 space-y-4">
        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}

        {/* Incident summary */}
        <div className="p-3 bg-[#0f1627] border border-[#1e2a42] rounded-xl">
          <p className="text-sm font-semibold text-white truncate">{incident.title}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <SeverityBadge value={incident.severity} />
            <StatusBadge value={incident.status} />
          </div>
        </div>

        {/* Severity / SLA */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#0f1627] border border-[#1e2a42] rounded-xl p-3">
            <p className="text-xs text-[#6b7fa3] mb-1">Severity</p>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${sev.dot}`} />
              <span className={`text-xs font-bold ${sevAccent.split(" ")[0]}`}>{sev.label}</span>
            </div>
          </div>
          <div className={`border rounded-xl p-3 ${sevAccent}`}>
            <p className="text-xs text-[#6b7fa3] mb-1">{deadline ? "Auto (overridden)" : "Suggested SLA"}</p>
            <p className="text-xs font-bold">{autoLabel}</p>
            <p className="text-xs opacity-60 mt-0.5">
              {suggested.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {" · "}
              {suggested.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
        </div>

        {/* Deadline */}
        <div className={`p-3 rounded-xl border ${!deadline ? "border-red-500/40 bg-red-500/5" : "border-[#1e2a42] bg-[#0f1627]"}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#a8b8d8] flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-[#6b7fa3]" />
              Override Deadline
              <span className="text-red-400">*</span>
            </p>
            {deadline && (
              <button onClick={() => setDeadline("")} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                <X className="w-3 h-3" /> clear
              </button>
            )}
          </div>
          <input
            type="datetime-local"
            value={deadline}
            min={new Date().toISOString().slice(0, 16)}
            onChange={e => setDeadline(e.target.value)}
            className={`${inputCls} [color-scheme:dark]`}
          />
          {deadline
            ? <p className="text-xs text-[#74c0fc] mt-1.5">✓ Custom deadline set</p>
            : <p className="text-xs text-red-400 mt-1.5">⚠ Required to assign</p>
          }
        </div>

        {/* Assignee */}
        <div>
          <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Assign To</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5878]" />
            <input value={assignee} onChange={e => setAssignee(e.target.value)}
              placeholder="e.g. Mark Bryan, Team Lead…"
              className={`${inputCls} pl-9`} />
          </div>
          {!assignee && <p className="text-xs text-[#6b7fa3] mt-1.5 italic">Leave blank to unassign</p>}
        </div>
      </div>
      <ModalFooter>
        <button onClick={handleSave} disabled={loading || !deadline}
          className={`flex-1 py-2.5 font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2 ${
            deadline
              ? "bg-[#3b5bdb] hover:bg-[#4c6ef5] text-white shadow-sm shadow-blue-900/40"
              : "bg-[#1a2540] text-[#4a5878] cursor-not-allowed"
          }`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
          {loading ? "Saving…" : "Confirm Assign"}
        </button>
        <button onClick={onClose} disabled={loading}
          className="px-5 py-2.5 bg-[#1a2540] hover:bg-[#243050] border border-[#243050] text-[#a8b8d8] text-sm rounded-xl transition-all font-medium">
          Cancel
        </button>
      </ModalFooter>
    </ModalShell>
  );
};

// ─── CREATE MODAL ─────────────────────────────────────────────────────────────

const CreateModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({ title: "", type: "website_down", severity: "P2", description: "", affectedUsers: "", assignee: "", reporter: "Admin" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return;
    setLoading(true); setError(null);
    try { const c = await api.create(form); onCreate(c); onClose(); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <ModalShell>
      <ModalHeader
        icon={<AlertTriangle className="w-4 h-4 text-red-400" />}
        title="Create Incident"
        subtitle="New service disruption"
        onClose={onClose}
      />
      <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Title *</label>
          <input value={form.title} onChange={e => set("title", e.target.value)}
            placeholder="e.g. Production website is down" className={inputCls} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Type</label>
            <select value={form.type} onChange={e => set("type", e.target.value)} className={`${selectCls} w-full`}>
              {INCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Severity</label>
            <select value={form.severity} onChange={e => set("severity", e.target.value)} className={`${selectCls} w-full`}>
              {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Description</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)}
            placeholder="What's happening? When did it start? What's affected?" rows={3}
            className={`${inputCls} resize-none`} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Affected Users</label>
            <input value={form.affectedUsers} onChange={e => set("affectedUsers", e.target.value)}
              placeholder="All, ~50 users…" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Assign To</label>
            <input value={form.assignee} onChange={e => set("assignee", e.target.value)}
              placeholder="e.g. Mark Bryan" className={inputCls} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-1.5">Reporter</label>
          <input value={form.reporter} onChange={e => set("reporter", e.target.value)}
            placeholder="Your name or username" className={inputCls} />
        </div>

        <div className="bg-[#0f1627] border border-[#1e2a42] rounded-xl p-4">
          <p className="text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-3">Severity Guide</p>
          <div className="grid grid-cols-2 gap-1.5">
            {SEVERITIES.map(s => (
              <div key={s.value} className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                <span className="text-xs font-bold text-[#a8b8d8] w-14">{s.label}</span>
                <span className="text-xs text-[#6b7fa3] truncate">
                  {s.value === "P1" && "Complete outage"}
                  {s.value === "P2" && "Major feature broken"}
                  {s.value === "P3" && "Partial issue"}
                  {s.value === "P4" && "Minor issue"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ModalFooter>
        <button onClick={submit} disabled={loading}
          className="flex-1 py-2.5 bg-[#e03131] hover:bg-[#f03e3e] disabled:opacity-60 text-white font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm shadow-red-900/30">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
          {loading ? "Creating…" : "Create Incident"}
        </button>
        <button onClick={onClose} disabled={loading}
          className="px-5 py-2.5 bg-[#1a2540] hover:bg-[#243050] border border-[#243050] text-[#a8b8d8] text-sm rounded-xl transition-all font-medium disabled:opacity-60">
          Cancel
        </button>
      </ModalFooter>
    </ModalShell>
  );
};

// ─── DETAIL PANEL ─────────────────────────────────────────────────────────────

const DetailPanel = ({ incident, onClose, onUpdate, onOpenAssign }) => {
  const [note, setNote]               = useState("");
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [loadingNote, setLoadingNote]   = useState(false);
  const [error, setError]             = useState(null);
  const sev      = getSeverity(incident.severity);
  const typ      = getType(incident.type);
  const timelines = incident.timelines ?? incident.timeline ?? [];

  const changeStatus = async (newStatus) => {
    setLoadingStatus(newStatus); setError(null);
    try { const u = await api.changeStatus(incident.id, newStatus); onUpdate(u); }
    catch (e) { setError(e.message); }
    finally { setLoadingStatus(null); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setLoadingNote(true); setError(null);
    try { const u = await api.addNote(incident.id, note); onUpdate(u); setNote(""); }
    catch (e) { setError(e.message); }
    finally { setLoadingNote(false); }
  };

  return (
    <ModalShell maxW="max-w-2xl">
      <div className={`h-1 w-full ${sev.strip}`} />
      <ModalHeader
        icon={React.cloneElement(typ.icon, { className: "w-4 h-4 text-[#74c0fc]" })}
        title={incident.title}
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono">{incident.incidentNumber ?? incident.id}</span>
          </span>
        }
        onClose={onClose}
        extra={
          <button
            onClick={() => { onClose(); onOpenAssign(incident); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1c3a6e]/60 hover:bg-[#1c3a6e] border border-[#3b5bdb]/30 text-[#74c0fc] text-xs font-semibold rounded-lg transition-all"
          >
            <UserCheck className="w-3.5 h-3.5" /> Assign
          </button>
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-5 max-h-[75vh]">
        {/* Badges row */}
        <div className="flex items-center gap-2 flex-wrap">
          <SeverityBadge value={incident.severity} />
          <StatusBadge value={incident.status} />
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}

        {/* Meta grid */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "Type",     val: typ.label,                     icon: React.cloneElement(typ.icon, { className: "w-3.5 h-3.5 text-[#4a5878]" }) },
            { label: "Assignee", val: incident.assignee || "—",      icon: <User className="w-3.5 h-3.5 text-[#4a5878]" /> },
            { label: "Reporter", val: incident.reporter,             icon: <User className="w-3.5 h-3.5 text-[#4a5878]" /> },
            { label: "Affected", val: incident.affectedUsers || "—", icon: <Shield className="w-3.5 h-3.5 text-[#4a5878]" /> },
            { label: "Created",  val: timeAgo(incident.createdAt),   icon: <Clock className="w-3.5 h-3.5 text-[#4a5878]" /> },
            { label: "Updated",  val: timeAgo(incident.updatedAt),   icon: <RefreshCw className="w-3.5 h-3.5 text-[#4a5878]" /> },
          ].map(({ label, val, icon }) => (
            <div key={label} className="bg-[#0f1627] border border-[#1e2a42] rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                {icon}
                <span className="text-xs text-[#6b7fa3] font-medium">{label}</span>
              </div>
              <p className="text-sm font-semibold text-white truncate">{val}</p>
            </div>
          ))}
        </div>

        {incident.description && (
          <div className="bg-[#0f1627] border border-[#1e2a42] rounded-xl p-4">
            <p className="text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-2">Description</p>
            <p className="text-sm text-[#a8b8d8] leading-relaxed">{incident.description}</p>
          </div>
        )}

        {/* Status change */}
        <div>
          <p className="text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-2">Change Status</p>
          <div className="flex flex-wrap gap-2">
            {STATUSES.filter(s => s.value !== incident.status).map(s => (
              <button key={s.value} onClick={() => changeStatus(s.value)}
                disabled={!!loadingStatus}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all hover:opacity-90 disabled:opacity-50 ${s.pill}`}>
                {loadingStatus === s.value ? <Loader2 className="w-3 h-3 animate-spin" /> : s.icon}
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <p className="text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-3">Timeline</p>
          <div className="space-y-3">
            {[...timelines].reverse().map((t, i) => (
              <div key={t.id ?? i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border ${
                    t.isNote ? "bg-violet-500/10 border-violet-500/25" : "bg-[#0f1627] border-[#1e2a42]"
                  }`}>
                    {t.isNote
                      ? <MessageSquare className="w-3 h-3 text-violet-400" />
                      : <Activity className="w-3 h-3 text-[#4a5878]" />}
                  </div>
                  {i < timelines.length - 1 && <div className="w-px flex-1 bg-[#1e2a42] mt-1" />}
                </div>
                <div className="pb-3 flex-1 min-w-0">
                  <p className={`text-sm ${t.isNote ? "text-violet-300 font-medium" : "text-[#a8b8d8]"}`}>{t.action}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[#6b7fa3]">{t.performedBy ?? t.user}</span>
                    <span className="text-xs text-[#243050]">·</span>
                    <span className="text-xs text-[#6b7fa3]">{timeAgo(t.createdAt ?? t.time)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add note */}
        <div>
          <p className="text-xs font-semibold text-[#6b7fa3] uppercase tracking-wider mb-2">Add Note</p>
          <div className="flex gap-2">
            <input value={note} onChange={e => setNote(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addNote()}
              placeholder="Add update or note..."
              className={`${inputCls} flex-1`} />
            <button onClick={addNote} disabled={loadingNote}
              className="px-4 py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-all flex items-center gap-1.5">
              {loadingNote && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Add
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function Incident() {
  const [incidents, setIncidents]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [showCreate, setShowCreate]         = useState(false);
  const [selected, setSelected]             = useState(null);
  const [assigning, setAssigning]           = useState(null);
  const [search, setSearch]                 = useState("");
  const [filterStatus, setFilterStatus]     = useState("ALL");
  const [filterSeverity, setFilterSeverity] = useState("ALL");

  const fetchIncidents = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await api.getAll({ status: filterStatus, severity: filterSeverity, search });
      setIncidents(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filterStatus, filterSeverity, search]);

  useEffect(() => {
    const t = setTimeout(fetchIncidents, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchIncidents]);

  const counts = {
    total:      incidents.length,
    open:       incidents.filter(i => i.status === "OPEN").length,
    inProgress: incidents.filter(i => i.status === "IN_PROGRESS").length,
    resolved:   incidents.filter(i => i.status === "RESOLVED").length,
    p1:         incidents.filter(i => i.severity === "P1").length,
  };

  const handleCreate       = (inc)     => setIncidents(p => [inc, ...p]);
  const handleUpdate       = (updated) => { setIncidents(p => p.map(i => i.id === updated.id ? updated : i)); setSelected(updated); };
  const handleAssignUpdate = (updated) => { setIncidents(p => p.map(i => i.id === updated.id ? updated : i)); setAssigning(null); };

  return (
    /* Dark navy blue — matches the screenshot exactly */
    <div className="min-h-screen bg-[#0b0f1c] font-sans">

      {error && <ErrorToast message={error} onDismiss={() => setError(null)} />}

      {/* Nav */}
      <div className="bg-[#0d1220]/95 backdrop-blur-sm border-b border-[#1e2a42] px-6 py-3 flex items-center justify-between sticky top-0 z-10 shadow-lg shadow-black/40">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-red-500/15 border border-red-500/25 flex items-center justify-center">
            <Shield className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <span className="text-sm font-bold text-white">OrcaHelpdesk</span>
            <span className="text-[#4a5878] mx-2 text-xs">/</span>
            <span className="text-sm font-semibold text-[#6b7fa3]">Incidents</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchIncidents} title="Refresh"
            className="p-2 hover:bg-[#1e2a42] rounded-xl transition-colors">
            <RefreshCw className={`w-4 h-4 text-[#6b7fa3] ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#e03131] hover:bg-[#f03e3e] text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-red-900/30">
            <Plus className="w-4 h-4" /> New Incident
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Total",       val: counts.total,      color: "text-white",       bg: "bg-[#131929] border-[#1e2a42]",                    dot: "bg-[#4a5878]"   },
            { label: "Open",        val: counts.open,       color: "text-sky-400",     bg: "bg-sky-500/10 border-sky-500/20",                  dot: "bg-sky-400"     },
            { label: "In Progress", val: counts.inProgress, color: "text-violet-400",  bg: "bg-violet-500/10 border-violet-500/20",            dot: "bg-violet-400"  },
            { label: "Resolved",    val: counts.resolved,   color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20",          dot: "bg-emerald-400" },
            { label: "P1 Critical", val: counts.p1,         color: "text-red-400",     bg: "bg-red-500/10 border-red-500/20",                  dot: "bg-red-400"     },
          ].map(s => (
            <div key={s.label} className={`border rounded-2xl p-4 flex flex-col gap-1 ${s.bg}`}>
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                <span className="text-xs text-[#6b7fa3] font-medium">{s.label}</span>
              </div>
              <p className={`text-3xl font-bold ${s.color}`}>{s.val}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5878]" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search incidents…"
              className={`${inputCls} pl-9 w-full`} />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={selectCls}>
            <option value="ALL">All Status</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} className={selectCls}>
            <option value="ALL">All Severity</option>
            {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* List */}
        <div className="space-y-2.5">
          {loading ? (
            <div className="flex items-center justify-center py-24 gap-3 text-[#4a5878]">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm font-medium">Loading incidents…</span>
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-24 border-2 border-dashed border-[#1e2a42] rounded-2xl bg-[#131929]">
              <AlertTriangle className="w-10 h-10 text-[#1e2a42] mx-auto mb-3" />
              <p className="text-[#a8b8d8] font-semibold">No incidents found</p>
              <p className="text-[#6b7fa3] text-sm mt-1">Try adjusting your filters or create a new one</p>
            </div>
          ) : incidents.map(inc => {
            const sev = getSeverity(inc.severity);
            const typ = getType(inc.type);
            const iconAccent = {
              P1: "text-red-400 bg-red-500/10 border-red-500/20",
              P2: "text-orange-400 bg-orange-500/10 border-orange-500/20",
              P3: "text-amber-400 bg-amber-500/10 border-amber-500/20",
              P4: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
            }[inc.severity] || "text-slate-400 bg-slate-500/10 border-slate-500/20";

            return (
              <div key={inc.id}
                className={`bg-[#131929] border rounded-2xl p-4 cursor-pointer group transition-all
                  hover:border-[#3b5bdb]/40 hover:shadow-xl hover:shadow-black/40 hover:-translate-y-0.5
                  flex items-start gap-4
                  ${inc.severity === "P1" ? "border-red-500/20" : "border-[#1e2a42]"}
                `}>
                <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${sev.strip}`} />

                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border ${iconAccent}`}
                  onClick={() => setSelected(inc)}
                >
                  {React.cloneElement(typ.icon, { className: `w-4 h-4 ${iconAccent.split(" ")[0]}` })}
                </div>

                <div className="flex-1 min-w-0" onClick={() => setSelected(inc)}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono font-bold text-[#6b7fa3]">
                      {inc.incidentNumber ?? inc.id}
                    </span>
                    <SeverityBadge value={inc.severity} />
                    <StatusBadge value={inc.status} />
                  </div>
                  <p className="text-sm font-bold text-white group-hover:text-[#74c0fc] transition-colors truncate">
                    {inc.title}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-xs text-[#6b7fa3] flex items-center gap-1">
                      <User className="w-3 h-3" />{inc.assignee || "Unassigned"}
                    </span>
                    <span className="text-xs text-[#6b7fa3] flex items-center gap-1">
                      <Clock className="w-3 h-3" />{timeAgo(inc.createdAt)}
                    </span>
                    {inc.affectedUsers && (
                      <span className="text-xs text-[#6b7fa3]">👥 {inc.affectedUsers} affected</span>
                    )}
                  </div>
                </div>

                {/* Assign button */}
                <button
                  onClick={e => { e.stopPropagation(); setAssigning(inc); }}
                  title="Assign"
                  className="flex-shrink-0 p-2 rounded-xl bg-[#1c3a6e]/40 hover:bg-[#1c3a6e]/80 border border-[#3b5bdb]/20 text-[#74c0fc] transition-all mt-0.5"
                >
                  <UserCheck className="w-4 h-4" />
                </button>

                <ChevronRight
                  onClick={() => setSelected(inc)}
                  className="w-4 h-4 text-[#2a3550] group-hover:text-[#6b7fa3] flex-shrink-0 mt-1 transition-colors"
                />
              </div>
            );
          })}
        </div>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
      {selected   && <DetailPanel incident={selected} onClose={() => setSelected(null)} onUpdate={handleUpdate} onOpenAssign={inc => setAssigning(inc)} />}
      {assigning  && <AssignModal incident={assigning} onClose={() => setAssigning(null)} onUpdate={handleAssignUpdate} />}
    </div>
  );
}